import { getColor, media } from '@startlibs/utils';
import React, { useState } from 'react'
import styled, { css } from 'styled-components';
import { PurviewFooter } from '../../components/PurviewFooter';
import { PageContainer, SectionHeading } from '../../components/PageLayout';
import { lighten, transparentize } from 'polished';
import {  Button, DropdownIcon, Icon, Loading, SplitColumnsContainer } from '@startlibs/components';
import { WithLazyResource } from '@startlibs/core';
import { IntlProvider, useIntl } from 'react-intl';
import { lazyLocale } from '../../components/I18NProvider';
import { lazyProviderInfo, lazyProviderInfoSec } from '../../components/WithProvider';
import { ToggleCheckbox, WithForm } from '@startlibs/form';
import { getJwt } from '../../hooks/useJwt';
import { jwtFormFetcher, jwtGetFetcher } from '../../utils/authFetch';
import { setNotification } from '../../components/Notifications';
import { willUseSuspense } from '../../hooks/useSuspense';

//
// STYLED COMPONENTS
//

export const TextButton = styled.a`
  text-decoration: none;
  color: ${getColor('main')};
  padding: 0.25rem 0.75rem;
  font-weight: 600;
  border-radius: 5px;
  user-select: none;
  position:relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  flex-shrink: 0;
  cursor: pointer;
  background-color: ${props => transparentize(0.9, getColor('main')(props))};
  & ~ & {
    margin-left: 0.75rem;
  }
  :hover {
    background-color: ${props => transparentize(0.85, getColor('main')(props))};
  }
  :active {
    background-color: ${props => transparentize(0.8, getColor('main')(props))};
  }
  ${props => props.focused && css`
    background-color: ${props => transparentize(0.9, getColor('main')(props))};
    color: ${getColor('main')};
  `}
  ${props => props.isLoading && css`
    background-color: ${props => transparentize(0.9, getColor('main')(props))};
    color: ${getColor('main')};
    pointer-events: none;
  `}
  ${props => props.started && css`
    background-color: ${props => lighten(0.4, props.theme.colors.success)};
    color: ${getColor('success')};
    pointer-events: none;
  `}
  ${props => props.disabled && css`
    color: ${getColor('gray90')};
    pointer-events: none;
  `}
  ${Loading} {
    display: inline-block;
    margin-right: 4px;
    vertical-align: sub;
  }
  ${Icon} {
    font-size: 18px;
    margin-right: 4px;
    vertical-align: bottom;
    padding: 8px;
  }
  ${DropdownIcon} {
    font-size: 10px;
    margin-left: 0.25rem;
    margin-right: 0;
    transform: translateY(1px);
    vertical-align: baseline;
  }
`
                                    
const Card = styled.div`
  padding: 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  margin: 0 auto;
  margin-bottom: 2rem;
  & ~ ${SectionHeading} {
    margin-top: 3rem;
  }
  ${media.max(520)`
    padding: 1.5rem;
  `}
`;

const useReportSettings = willUseSuspense(() => jwtGetFetcher(getJwt())("/api/providerInfoSec").then((data) => ({ reportSignatureRequired: data.reportSignatureRequired })));



export const CaseReportConfigPage = () => {
  const providerInfo = lazyProviderInfo.read();
  const defaultLanguage = providerInfo?.preferredLanguage?.defaultLanguage

  const [pageLanguage, setPageLanguage] = useState(defaultLanguage)

  return <WithLazyResource fallback={null} value={lazyLocale.use(pageLanguage)} >{([,messages]) =>
    <IntlProvider messages={messages} locale={pageLanguage} defaultLocale="en-US">
      <CaseReportSettings pageLanguage={pageLanguage} setPageLanguage={setPageLanguage}/>
    </IntlProvider>
  }</WithLazyResource>

}
  
//
// THE COMPONENT
//
export const CaseReportSettings = ({ pageLanguage, setPageLanguage}) => {
  useIntl();
  const [reportSettings, setReportSettings] = React.useState(useReportSettings());

  return (
    <>
      <PageContainer css="max-width: 100%;">
        <Card style={{position: 'relative'}}>
          <h3>Case report settings</h3>
          <p>Set required information for reports.</p>
          <WithForm
            values={reportSettings}
            onSuccess={(values) => {
              setReportSettings(values);
              lazyProviderInfoSec.refresh();
              setNotification("Case report settings saved.");
            }}
            action={jwtFormFetcher(getJwt())("/api/admin/reportSignatureRequired", {method: "PUT"})}
          >{form => <>
              <SplitColumnsContainer 
                margin="big"
                viewportMinWidth={950}
                css="padding-top: 1.5rem;"
              >
                <div>
                  <ToggleCheckbox
                    label="Require expert signature"
                    path="reportSignatureRequired"  
                  />
                </div>
                <div>
                  
                </div>
              </SplitColumnsContainer>
                <div css="text-align: right;">
                    {form.hasChanged && <Button onClick={form.resetForm}>Reset changes</Button>}
                    <Button disabled={!form.hasChanged} type="submit" highlight isLoading={form.isLoading}>Save changes</Button>
                </div>
            </>}
          </WithForm>
        </Card>
      </PageContainer>
      <PurviewFooter />
    </>
  );
};